import React, { useState } from "react";
import {
    Fab,
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    IconButton,
    Grow,
    ClickAwayListener,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import config from "../config";
import themeColors from "../theme";

const { teal, darkTeal } = themeColors;

export default function HelpWidget() {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);

    const handleSend = async () => {
        if (!message.trim() || sending) return;
        setSending(true);
        try {
            const token = localStorage.getItem("token");
            await axios.post(
                `${config.SERVER_URL}/api/feedback`,
                { message },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSubmitted(true);
            setMessage("");
            setTimeout(() => {
                setSubmitted(false);
                setOpen(false);
            }, 2500);
        } catch (err) {
            console.error("Failed to send feedback:", err);
        } finally {
            setSending(false);
        }
    };

    return (
        <Box sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1300 }}>
            {/* Chat popup */}
            {open && (
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <Grow in={open} style={{ transformOrigin: "bottom right" }}>
                        <Paper
                            elevation={8}
                            sx={{
                                width: 340,
                                mb: 2,
                                borderRadius: 3,
                                overflow: "hidden",
                            }}
                        >
                            {/* Header */}
                            <Box
                                sx={{
                                    background: `linear-gradient(135deg, ${teal}, ${darkTeal})`,
                                    color: "#fff",
                                    px: 2.5,
                                    py: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Typography fontWeight={600} fontSize={15}>
                                    Questions? We're here to help!
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => setOpen(false)}
                                    sx={{ color: "#fff" }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            {/* Body */}
                            <Box sx={{ p: 2.5 }}>
                                {submitted ? (
                                    <Typography
                                        sx={{ py: 4, textAlign: "center", color: teal }}
                                        fontWeight={600}
                                    >
                                        Thanks! We'll get back to you soon.
                                    </Typography>
                                ) : (
                                    <>
                                        <TextField
                                            multiline
                                            minRows={4}
                                            maxRows={6}
                                            fullWidth
                                            placeholder="Type your message here..."
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSend();
                                                }
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2,
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: teal,
                                                    },
                                                },
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                mt: 1.5,
                                            }}
                                        >
                                            <Button
                                                variant="contained"
                                                endIcon={<SendIcon />}
                                                onClick={handleSend}
                                                disabled={!message.trim() || sending}
                                                sx={{
                                                    backgroundColor: teal,
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    borderRadius: 2,
                                                    px: 3,
                                                    "&:hover": {
                                                        backgroundColor: darkTeal,
                                                    },
                                                }}
                                            >
                                                Send
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Paper>
                    </Grow>
                </ClickAwayListener>
            )}

            {/* Floating Action Button */}
            <Fab
                onClick={() => setOpen((prev) => !prev)}
                sx={{
                    backgroundColor: teal,
                    color: "#fff",
                    width: 60,
                    height: 60,
                    boxShadow: "0 4px 14px rgba(0,127,126,0.45)",
                    "&:hover": {
                        backgroundColor: darkTeal,
                        transform: "scale(1.08)",
                    },
                    transition: "all 0.2s ease",
                }}
            >
                {open ? (
                    <CloseIcon sx={{ fontSize: 28 }} />
                ) : (
                    <HelpOutlineIcon sx={{ fontSize: 30 }} />
                )}
            </Fab>
        </Box>
    );
}
